import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ICourierSelectedProps } from '../CourierCard';
import { COURIER_STATUS, TCourier } from '../../services/api';
import CourierPageHeader from '../CourierPageHeader';
import { CourierBottomSheet, EBottomSheetAction, TBottomSheetAction } from '../CourierBottomSheet';
import { SpringEvent } from 'react-spring-bottom-sheet/dist/types';
import { CourierDialogPopup } from '../CourierDialogPopup';
import { CourierListEmptyPlaceholder } from '../CourierListEmptyPlaceholder';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import CourierPendingRequestDemandList from '../CourierPendingRequestDemandList';
import { useQueryFetchCourierList, useQueryGetTipSettings } from '../../queries';
import {
  trackManageCourierOnSettingsClick,
  trackManageCouriersActiveCourierCardClick,
  trackManageCouriersActiveCourierList,
  trackManageCouriersPendingCourierCardClick,
  trackManageCouriersPendingCourierList,
} from '../../tracking/courier-card';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import { encryptHash } from '@src/shared/utils';
import useFMFeatureFlag from '@src/shared/hooks/use-fm-featureflag';
import { JETFM_TIPS_RESTRICTED_COUNTRY_LIST } from '@src/shared/models';
import useAnnouncementBannerLogic from '@src/shared/hooks/use-anouncement-banner-logic';
import utilStorage from '@src/shared/services/util-storage';
import styles from './CourierListMobile.module.scss';
import PIEText from '@src/shared/components/PIEText';
import PIETabsPanel from '@src/shared/components/PIETabsPanel';
import { QRCodeScreenTypes } from '../CourierQRCodeManager/QRCodeCommonComponentEnums';
import CourierQRCodeManager from '../CourierQRCodeManager';
import { useTutorialActions } from '@src/shared/context/tutorial';
import { TUTORIAL_OCF } from '@src/shared/components/Tutorial/TutorialsContainer';
import OCFTutorial from '@src/shared/components/Tutorial/tutorials/OCFTutorial';
import OCFTutorialPopup from '@src/shared/components/Tutorial/tutorials/OCFTutorial/components/OCFTutorialPopup';
import useOCFStatus from '@src/shared/hooks/use-ocf-status';
import CourierListTabMenu from '../CourierListTabMenu';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import CourierListFiltered from '../CourierListFiltered';
import PIEButton from '@src/shared/components/PIEButton';
import { IconSettings } from '@justeattakeaway/pie-icons-react';
import { set } from 'lodash';
import { useBoundDashboardStore } from '@src/pages/DeliveryDashboard/store/dashboardGlobalStore';

export enum TAB_SELECTION {
  ACTIVE = 'ACTIVE',
  PENDING = 'PENDING',
}

function CourierListMobile({ onError }: { onError: any }) {
  const { t } = useTranslation();
  const [tabSelectionValue, setTabSelectionValue] = React.useState<TAB_SELECTION>(
    TAB_SELECTION.ACTIVE
  );
  const [qrCodeManagerScreenType, setQRCodeManagerScreenType] = useState<QRCodeScreenTypes | null>(
    null
  );
  const [isOCFTutorialSkipped, setIsOCFTutorialSkipped] = useState<boolean | null>(null);
  const { isResolutionMobile } = useDeviceDetect();
  const [selectedCourier, setSelectedCourier] = useState<ICourierSelectedProps | null>(null);
  const [isPendingCourier, setIsPendingCourier] = useState(false);
  const [courierListData, setCourierListData] = useState<TCourier[]>([]);
  const { restaurant } = useKeycloakUserState();
  const [bottomSheetActionType, setBottomSheetActionType] = useState<TBottomSheetAction>();
  const { isMobileDevice, isTablet } = useDeviceDetect();
  const isMobile = isMobileDevice && !isTablet;
  const [isTipsActive, setIsTipsActive] = useState(false);
  const tipsRestrictedCountryList = useFMFeatureFlag(JETFM_TIPS_RESTRICTED_COUNTRY_LIST);
  const { startTutorial } = useTutorialActions();
  const { finalizeBanner } = useAnnouncementBannerLogic({
    trackingTitle: '',
    onFinalize: () => {
      utilStorage.setManageCourierSettingsTutorialSeen('NO_SHOW');
    },
  });

  const { isOCFEnabled } = useOCFStatus();

  const dashboardCouriers = useBoundDashboardStore((state) => state.couriers);

  useEffect(() => {
    if (!restaurant || !tipsRestrictedCountryList) {
      return;
    }
    if (!tipsRestrictedCountryList.includes(restaurant?.country)) {
      setIsTipsActive(true);
    }
  }, [restaurant, tipsRestrictedCountryList]);

  useEffect(() => {
    if (selectedCourier && bottomSheetActionType?.source !== selectedCourier.hash) {
      setBottomSheetActionType({
        type: isPendingCourier ? EBottomSheetAction.ReSend : EBottomSheetAction.View,
        source: selectedCourier,
      });
    }
    return () => setBottomSheetActionType(undefined);
  }, [selectedCourier, isPendingCourier]);

  useEffect(() => {
    if (isOCFTutorialSkipped !== null) {
      startTutorial(TUTORIAL_OCF);
    }
  }, [isOCFTutorialSkipped]);

  const sendAnalyticsData = (selectedTab: TAB_SELECTION) => {
    if (selectedTab === TAB_SELECTION.ACTIVE) {
      const activeListLength = courierListData.filter(
        (a) => a.status === COURIER_STATUS.CONNECTED
      ).length;
      trackManageCouriersActiveCourierList(
        activeListLength,
        restaurant?.language as any,
        restaurant?.country as any
      );
    }

    if (selectedTab === TAB_SELECTION.PENDING) {
      const pendingListLength = courierListData.filter(
        (a) =>
          a.status !== COURIER_STATUS.CONNECTED &&
          a.status !== COURIER_STATUS.DECLINED &&
          a.status !== COURIER_STATUS.PENDING_RESTAURANT_ACCEPTANCE
      ).length;
      trackManageCouriersPendingCourierList(
        pendingListLength,
        restaurant?.language as any,
        restaurant?.country as any
      );
    }
  };

  useEffect(() => {
    if (!courierListData.length) {
      return;
    }
    sendAnalyticsData(tabSelectionValue);
  }, [tabSelectionValue]);

  const {
    status,
    data,
    refetch: refetchCourierList,
  } = useQueryFetchCourierList({
    onError: () => {
      onError && onError();
      console.warn('ERROR: Fetching courier list failed');
    },

    onSuccess: (data: TCourier[]) => {
      if (!courierListData.length) {
        sendAnalyticsData(tabSelectionValue);
      }
      setCourierListData(data);
    },
  });

  useEffect(() => {
    if (!dashboardCouriers.length || !courierListData.length) {
      return;
    }

    const courierListIds = courierListData.map((courier) => courier.id);
    const hasNewCourier = dashboardCouriers.some(
      (dashboardCourier: { id: string }) => !courierListIds.includes(dashboardCourier.id)
    );

    if (hasNewCourier) {
      refetchCourierList();
    }
  }, [dashboardCouriers]);

  const tipSettings = useQueryGetTipSettings({
    onError: () => {
      onError && onError();
      console.warn('ERROR: Fetching restaurant settings failed');
    },
  });
  const handleOnSettings = () => {
    trackManageCourierOnSettingsClick(restaurant?.country as any, restaurant?.language as any);
    setBottomSheetActionType({
      type: EBottomSheetAction.Tips,
      source: { text: 'external' },
    });
  };

  if (status === 'loading') {
    return <PIEText size="var(--xds-font-size-14)" text={`${t('misc.loading', 'loading')}...`} />;
  }

  const handleOnButtonAction = (action: TBottomSheetAction) => {};

  const handleOnBottomSheetOpen = (event: SpringEvent) => {};

  const visibleCouriers = courierListData.filter(
    (courier) =>
      (!isOCFEnabled && courier.status !== COURIER_STATUS.PENDING_RESTAURANT_ACCEPTANCE) ||
      (isOCFEnabled && courier.isVerified !== false)
  );

  const isCourierExists = visibleCouriers.length > 0;

  const handleOnBottomSheetClose = (event: SpringEvent) => {
    if (event.type === 'CLOSE' && bottomSheetActionType?.type === EBottomSheetAction.View) {
      setSelectedCourier(null);
      setBottomSheetActionType(undefined);
    }
  };

  const handleOnDialogsClose = () => {
    setBottomSheetActionType(undefined);
    selectedCourier?.hash !== null ? setSelectedCourier(null) : null;
  };

  const handleSelectedCourierId = (courier: ICourierSelectedProps, isCourierPending: boolean) => {
    setSelectedCourier(null);
    if (courier.hash !== null) {
      setSelectedCourier({ ...courier });
      setIsPendingCourier(isCourierPending);

      !isCourierPending
        ? trackManageCouriersActiveCourierCardClick(
            restaurant?.country as any,
            restaurant?.language as any,
            encryptHash(courier?.email as string)
          )
        : trackManageCouriersPendingCourierCardClick(
            restaurant?.country as any,
            restaurant?.language as any,
            encryptHash(courier?.email as string)
          );
    }
  };

  const handleAddCourierAction = () => {
    setBottomSheetActionType({
      type: EBottomSheetAction.Add,
      source: { text: 'external' },
    });
  };

  const createMockDataForOCFTutorial = () => {
    const mockData: TCourier = {
      id: 'xxxxxxxx-42da-475c-aa5f-f2dcfc9ab21c',
      hash: 'xxxxxxx-79e2-3987-b195-6ec38392ef78',
      email: 'example.courier@examplemail.com',
      firstName: 'Courier Name',
      lastName: 'Courier Last Name',
      status: COURIER_STATUS.CONNECTED,
      settings: {
        showUnassignedOrders: false,
      },
      dateAdded: new Date(),
    };
    const newCourierListData = [mockData, ...courierListData];
    setCourierListData(newCourierListData);
  };

  const removeMockCourierDataForOCFTutorial = () => {
    refetchCourierList();
  };

  return (
    <>
      {isOCFEnabled && (
        <OCFTutorialPopup
          onTutorialStart={() => {
            setIsOCFTutorialSkipped(false);
          }}
          onTutorialSkip={() => {
            setIsOCFTutorialSkipped(true);
          }}
        />
      )}
      <OCFTutorial
        startFromStep={isOCFTutorialSkipped ? 7 : 1}
        onTutorialStart={() => {
          createMockDataForOCFTutorial();
        }}
        onTutorialEnd={() => {
          removeMockCourierDataForOCFTutorial();
          setIsOCFTutorialSkipped(null);
        }}
      />
      <div className={styles.manageCouriersContainer}>
        <div className={styles.manageCouriersContentWrapper}>
          <div className={styles.manageCouriersCentered}>
            <CourierPageHeader
              isFunctional={!!courierListData.length}
              onSettings={handleOnSettings}
              onShowQRCode={() => {
                setQRCodeManagerScreenType(QRCodeScreenTypes.SHOW_QR_CODE);
              }}
              onDownloadQRCode={() => {
                setQRCodeManagerScreenType(QRCodeScreenTypes.DOWNLOAD_QR_CODE);
              }}
              onLegacyAddCourier={handleAddCourierAction}
            />
            {(courierListData.length && (
              <div style={{ display: 'flex' }}>
                {!isOCFEnabled && (
                  <CourierListTabMenu
                    tabSelectionValue={tabSelectionValue}
                    onTabSelect={setTabSelectionValue}
                  />
                )}
                {isTipsActive && !isOCFEnabled && isResolutionMobile ? (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <PIEButton
                      id="manage-courier-tip-settings-button"
                      onClick={() => {
                        handleOnSettings();
                        finalizeBanner();
                      }}
                      className={styles.iconButton}
                      aria-label="settings"
                      icon={<IconSettings size="s" />}
                      text={''}
                      size={'small'}
                      mode={'secondary'}
                      type={'button'}
                    />
                  </div>
                ) : null}
              </div>
            )) || <></>}
          </div>
        </div>

        <PIEComponentUtil marginBottom={'var(--xds-spacing-c)'}>
          <CourierPendingRequestDemandList
            couriers={courierListData
              .sort((a, b) => {
                return new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime();
              })
              .filter(
                (courier) =>
                  courier.status === COURIER_STATUS.PENDING_RESTAURANT_ACCEPTANCE ||
                  courier.isVerified === false
              )}
          />
        </PIEComponentUtil>
        {!visibleCouriers.length && (
          <CourierListEmptyPlaceholder
            onAction={handleAddCourierAction}
            onShowQRCode={() => {
              setQRCodeManagerScreenType(QRCodeScreenTypes.SHOW_QR_CODE);
            }}
            onDownloadQRCode={() => {
              setQRCodeManagerScreenType(QRCodeScreenTypes.DOWNLOAD_QR_CODE);
            }}
          />
        )}

        <div className={styles.manageCouriersCentered}>
          <div className={styles.manageCouriersList} id="manage-courier-list">
            {(isCourierExists && isOCFEnabled && (
              <PIEComponentUtil marginBottom="var(--xds-spacing-e)">
                <PIEText
                  size="var(--xds-font-size-20)"
                  fontWeight="var(--xds-font-weight-extrabold)"
                  text={t('manage_couriers.ALL_COURIERS_TITLE')}
                />
              </PIEComponentUtil>
            )) || <></>}

            <PIETabsPanel id="manage-courier-tabs" isSelected={true}>
              <CourierListFiltered
                onCourierSelected={handleSelectedCourierId}
                courierListData={courierListData}
                tabSelectionValue={!isOCFEnabled ? tabSelectionValue : TAB_SELECTION.ACTIVE}
              />
            </PIETabsPanel>
          </div>
        </div>
        {isMobile ? (
          <div style={{ position: 'absolute', bottom: 0 }}>
            <CourierBottomSheet
              onDialogsClose={handleOnDialogsClose}
              onButtonAction={handleOnButtonAction}
              onBottomSheetClose={handleOnBottomSheetClose}
              onBottomSheetOpen={handleOnBottomSheetOpen}
              actionType={bottomSheetActionType}
              resturantSettings={{ showTips: tipSettings.data?.showTips as any }}
              dataObject={
                courierListData.find(
                  (courier) => courier.hash === selectedCourier?.hash
                ) as TCourier
              }
            />
          </div>
        ) : (
          <CourierDialogPopup
            onDialogClose={handleOnDialogsClose}
            actionType={bottomSheetActionType}
            resturantSettings={{ showTips: tipSettings.data?.showTips as any }}
            dataObject={
              courierListData.find((courier) => courier.hash === selectedCourier?.hash) as TCourier
            }
          />
        )}
      </div>

      {isOCFEnabled && (
        <CourierQRCodeManager
          initialScreenType={qrCodeManagerScreenType}
          onClose={() => setQRCodeManagerScreenType(null)}
        />
      )}
    </>
  );
}

export default CourierListMobile;
